import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/id";
import { FaCubes, FaPlus } from "react-icons/fa";
import Api from "../../api";
import DataOutMaterial from "../../components/materials/DataOutMaterial";
import { SipCalendar, SipDropdown, SipInput, SipSearch, SipTextarea } from "../../components/form/SipControls";

dayjs.locale("id");

const emptyForm = () => ({
  material_id: "",
  tgl_pakai: dayjs().format("YYYY-MM-DD"),
  jumlah_pakai: "",
  lokasi_pakai: "",
  teknisi: "",
  keterangan: "",
});

function MaterialOut() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Api.get("/mon/material-outs", {
        params: { per_page: 200, sort_by: "tgl_pakai", sort_order: "desc" },
      });
      setRows(res.data?.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Gagal memuat material keluar");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const [materials, teams] = await Promise.all([
        Api.get("/mon/material/options"),
        Api.get("/mon/teams", { params: { per_page: 200 } }),
      ]);
      setMaterialOptions(materials.data?.material || []);
      setTeamOptions(teams.data?.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Gagal memuat pilihan material atau pengambil");
    }
  }, []);

  useEffect(() => {
    loadData();
    loadOptions();
  }, [loadData, loadOptions]);

  const selected = materialOptions.find((item) => String(item.id) === String(form.material_id));
  const editingRow = rows.find((row) => String(row.id) === String(editingId));
  const stokKredit = editingRow && String(editingRow.material_id) === String(form.material_id)
    ? Number(editingRow.jumlah_pakai) || 0
    : 0;
  const stokTersedia = selected == null ? null : (Number(selected.stok_sekarang) || 0) + stokKredit;
  const stokHabis = stokTersedia != null && stokTersedia <= 0;

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((item) =>
      [item.nama_material, item.lokasi_pakai, item.teknisi, item.keterangan, item.satuan]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, searchTerm]);

  const totalJumlah = filtered.reduce((sum, item) => sum + (Number(item.jumlah_pakai) || 0), 0);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (id) => {
    const item = rows.find((row) => String(row.id) === String(id));
    if (!item) return;
    setEditingId(item.id);
    setForm({
      material_id: item.material_id ? String(item.material_id) : "",
      tgl_pakai: item.tgl_pakai ? dayjs(item.tgl_pakai).format("YYYY-MM-DD") : "",
      jumlah_pakai: item.jumlah_pakai != null ? String(item.jumlah_pakai) : "",
      lokasi_pakai: item.lokasi_pakai || "",
      teknisi: item.teknisi || "",
      keterangan: item.keterangan || "",
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus pemakaian ini? Stok gudang akan dikembalikan.")) return;
    try {
      setLoading(true);
      await Api.delete(`/mon/material-outs/${id}`);
      toast.success("Pemakaian dihapus");
      await loadData();
      await loadOptions();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menghapus pemakaian");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const next = {};
    if (!form.material_id) next.material_id = "Material wajib dipilih";
    else if (stokHabis) next.material_id = "Stok material habis, pemakaian tidak bisa ditambahkan";
    if (!form.tgl_pakai) next.tgl_pakai = "Tanggal pakai wajib diisi";
    if (!form.jumlah_pakai || Number(form.jumlah_pakai) <= 0) {
      next.jumlah_pakai = "Jumlah pakai harus lebih dari 0";
    } else if (stokTersedia != null && !stokHabis && Number(form.jumlah_pakai) > stokTersedia) {
      next.jumlah_pakai = `Jumlah pakai melebihi stok gudang (${stokTersedia})`;
    }
    if (!String(form.lokasi_pakai || "").trim()) next.lokasi_pakai = "Lokasi pakai wajib diisi";
    if (!String(form.teknisi || "").trim()) next.teknisi = "Pengambil wajib dipilih";
    setErrors(next);
    if (Object.keys(next).length) return;

    const payload = {
      material_id: form.material_id,
      tgl_pakai: form.tgl_pakai,
      jumlah_pakai: Number(form.jumlah_pakai),
      lokasi_pakai: form.lokasi_pakai.trim(),
      teknisi: form.teknisi,
      keterangan: form.keterangan?.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await Api.put(`/mon/material-outs/${editingId}`, payload);
        toast.success("Pemakaian diperbarui");
      } else {
        await Api.post("/mon/material-outs", payload);
        toast.success("Pemakaian tersimpan");
      }
      setIsModalOpen(false);
      await loadData();
      await loadOptions();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menyimpan pemakaian");
    } finally {
      setSaving(false);
    }
  };

  const pengambilOptions = useMemo(() => {
    const names = teamOptions.map((item) => item.nama).filter(Boolean);
    if (form.teknisi && !names.includes(form.teknisi)) names.unshift(form.teknisi);
    return names.map((nama) => ({ value: nama, label: nama }));
  }, [teamOptions, form.teknisi]);

  return (
    <div className="space-y-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="bg-primary px-6 py-8 md:px-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="text-white">
                <div className="mb-2 flex items-center gap-3">
                  <div className="rounded-lg bg-white/20 p-2">
                    <FaCubes className="text-xl" />
                  </div>
                  <h1 className="text-2xl font-bold md:text-3xl">Material Keluar</h1>
                </div>
                <p className="text-sm text-white/90 md:text-base">
                  Catat pemakaian material gedung. Stok gudang berkurang sesuai jumlah pakai. Master material tetap di stelkpro.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="rounded-xl border border-white/20 bg-white/10 px-4 py-3 font-medium text-white hover:bg-white/20"
              >
                Kembali
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-lg">
            <p className="text-sm font-medium text-gray-600">Catatan</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{filtered.length}</p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-lg">
            <p className="text-sm font-medium text-gray-600">Total pakai</p>
            <p className="mt-1 text-2xl font-bold text-stone-700">{totalJumlah}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
          <div className="flex flex-col gap-3 min-[480px]:flex-row min-[480px]:items-center">
            <SipSearch
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Cari material, lokasi, pengambil..."
              className="w-full min-w-0 min-[480px]:flex-1"
            />
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary px-4 text-sm font-medium text-white shadow-lg min-[480px]:w-auto"
            >
              <FaPlus />
              Tambah Pemakaian
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg">
          <DataOutMaterial
            rows={filtered}
            loading={loading}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        </div>
      </div>

      <Dialog
        visible={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        header={editingId ? "Edit Pemakaian" : "Tambah Pemakaian"}
        style={{ width: "95vw", maxWidth: "720px" }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Batal" className="p-button-text" onClick={() => setIsModalOpen(false)} disabled={saving} />
            <Button label={editingId ? "Update" : "Simpan"} icon="pi pi-save" onClick={handleSubmit} loading={saving} disabled={stokHabis} />
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Material</label>
            <SipDropdown
              name="material_id"
              value={form.material_id}
              onChange={handleChange}
              filter
              placeholder="Pilih material"
              options={materialOptions.map((item) => ({
                value: String(item.id),
                label: `${item.nama_material} (stok ${item.stok_sekarang} ${item.satuan || ""}${Number(item.stok_sekarang) <= 0 ? ", habis" : ""})`.trim(),
              }))}
            />
            {errors.material_id && <p className="mt-1 text-xs text-red-500">{errors.material_id}</p>}
            {selected && (
              <p className={`mt-1 text-xs ${stokHabis ? "text-red-600" : "text-gray-500"}`}>
                {stokHabis
                  ? "Stok gudang habis. Pemakaian tidak bisa ditambahkan."
                  : `Stok gudang: ${stokTersedia} ${selected.satuan || ""}. Minimal: ${selected.stok_minimal}.`}
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Tanggal pakai</label>
            <SipCalendar name="tgl_pakai" value={form.tgl_pakai} onChange={handleChange} />
            {errors.tgl_pakai && <p className="mt-1 text-xs text-red-500">{errors.tgl_pakai}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Jumlah pakai</label>
            <SipInput name="jumlah_pakai" type="number" min="1" value={form.jumlah_pakai} onChange={handleChange} />
            {errors.jumlah_pakai && <p className="mt-1 text-xs text-red-500">{errors.jumlah_pakai}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Lokasi pakai</label>
            <SipInput name="lokasi_pakai" value={form.lokasi_pakai} onChange={handleChange} />
            {errors.lokasi_pakai && <p className="mt-1 text-xs text-red-500">{errors.lokasi_pakai}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Pengambil</label>
            <SipDropdown
              name="teknisi"
              value={form.teknisi}
              onChange={handleChange}
              filter
              placeholder="Pilih pengambil"
              options={pengambilOptions}
            />
            {errors.teknisi && <p className="mt-1 text-xs text-red-500">{errors.teknisi}</p>}
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Keterangan</label>
            <SipTextarea name="keterangan" value={form.keterangan} onChange={handleChange} rows={2} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default MaterialOut;
